// =============================================================================
// domain/riskOfRuin.ts — the chance this account is dead inside a month.
// -----------------------------------------------------------------------------
// A Monte-Carlo resample of the account's own daily P&L. Each simulated day
// draws one real trading day at random, with replacement, and the run ends the
// moment the balance touches the trailing floor.
//
// ── 🚨 THE FLOOR TRAILS THE PEAK, AND THAT IS THE WHOLE MODEL ──
//
// A prop account's max drawdown is measured from its high-water mark, not from
// where it started. Modelling a fixed floor would understate ruin badly for the
// case that actually kills combines: run up, give it back. The lock mechanic
// (`trail-then-freeze-at-start`, `…-plus-pad`, `static`) stops the floor rising
// past a cap, and it is read from the account's own rules rather than assumed.
//
// ── 🚨 THE RANDOM SOURCE IS INJECTED (D26) ──
//
// `Math.random()` in here would make every render produce a different number for
// the same book, and no test could ever pin one. The caller supplies the source;
// the report supplies a SEEDED one so the same account and window always show
// the same figure. A risk number that moves on its own is not a risk number.
//
// ── ⚠ TWO SAMPLE FLOORS, AND THEY SAY DIFFERENT THINGS ──
//
//   below MIN_DAYS     no figure at all — a resample of one day is that day
//   below STABLE_DAYS  a figure, marked PRELIMINARY
//
// The old client publishes early on purpose so the number develops as the trader
// logs, and flags it until the pool is real. Reproduced, because hiding it until
// day 20 means the metric appears out of nowhere already saying something bad.
// =============================================================================

/** Below this there is no pool to resample. */
export const ROR_MIN_DAYS = 2;
/** Below this the figure is a rough indication, and says so. */
export const ROR_STABLE_DAYS = 20;
/** The old client's report settings: a month ahead, 1500 runs. */
export const ROR_HORIZON_DAYS = 30;
export const ROR_SIMS = 1500;

/**
 * A deterministic source, so one account and window always give one number.
 *
 * ⚠ NOT for cryptography and never used as such — this is a spread of possible
 * futures, and its only requirement is that it does not repeat visibly and does
 * not move between renders.
 */
export function seededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let s = h >>> 0;
  return () => {
    s ^= s << 13; s >>>= 0;
    s ^= s >>> 17;
    s ^= s << 5; s >>>= 0;
    return s / 4294967296;
  };
}

export interface RuinRules {
  /** The trailing drawdown in dollars. */
  maxDrawdown: number;
  /** The profit target that ends a run early, or null for a funded account. */
  profitTarget: number | null;
  lockMechanic?: string | null | undefined;
  lockPad?: number | null | undefined;
}

export interface LockParams { lock: boolean; lockBuffer: number }

/**
 * ⚠ AN UNKNOWN MECHANIC LOCKS, matching the old client's own comment: "treat as
 * lock-at-start to be conservative." A wrong guess in the other direction lets
 * the floor keep trailing and reports a SAFER account than the rules allow.
 */
export function lockParams(rules: Pick<RuinRules, 'lockMechanic' | 'lockPad'>): LockParams {
  const m = rules.lockMechanic || 'trail-forever';
  if (m === 'trail-forever') return { lock: false, lockBuffer: 0 };
  if (m === 'trail-then-freeze-at-start') return { lock: true, lockBuffer: 0 };
  if (m === 'trail-then-freeze-at-start-plus-pad') {
    return { lock: true, lockBuffer: Number(rules.lockPad) || 0 };
  }
  return { lock: true, lockBuffer: 0 };
}

export interface RuinInput {
  /** One entry per REAL trading day. Never per trade — see `dailyPnl`. */
  days: readonly number[];
  start: number;
  rules: RuinRules;
  rand: () => number;
  horizon?: number;
  sims?: number;
}

export interface RuinResult {
  /** Percent of runs that touched the floor. */
  blowPct: number;
  /** Percent that reached the profit target first. */
  passPct: number;
  days: number;
  /** True while the pool is under `ROR_STABLE_DAYS`. */
  preliminary: boolean;
  sims: number;
}

/**
 * @returns null when the pool is too thin to resample at all.
 */
export function riskOfRuin(input: RuinInput): RuinResult | null {
  const { days, start, rules, rand } = input;
  if (days.length < ROR_MIN_DAYS) return null;

  const sims = input.sims ?? ROR_SIMS;
  const horizon = input.horizon ?? ROR_HORIZON_DAYS;
  const dd = rules.maxDrawdown;
  // ⚠ A missing or zero drawdown would make the floor the starting balance and
  // report near-certain ruin on a normal account. Refuse instead of inventing a
  // limit the firm never set.
  if (!(dd > 0)) return null;

  const { lock, lockBuffer } = lockParams(rules);
  const lockCap = start + lockBuffer;
  // ⚠ `null` target = FUNDED. Nothing ends a run early, so every simulation runs
  // the full horizon and only ruin can stop it.
  const target = rules.profitTarget !== null && rules.profitTarget > 0
    ? rules.profitTarget : Infinity;

  let blow = 0; let pass = 0;
  for (let s = 0; s < sims; s += 1) {
    let bal = start;
    let peak = start;
    let floor = start - dd;
    for (let d = 0; d < horizon; d += 1) {
      bal += days[Math.floor(rand() * days.length) % days.length]!;
      if (bal <= floor) { blow += 1; break; }
      if (bal - start >= target) { pass += 1; break; }
      if (bal > peak) {
        peak = bal;
        floor = peak - dd;
        if (lock && floor > lockCap) floor = lockCap;
      }
    }
  }

  return {
    blowPct: (blow / sims) * 100,
    passPct: (pass / sims) * 100,
    days: days.length,
    preliminary: days.length < ROR_STABLE_DAYS,
    sims,
  };
}

/**
 * Net P&L per trading DAY, which is the unit the model resamples.
 *
 * ⚠ PER DAY, NEVER PER TRADE. Resampling trades would model a trader who takes
 * one position a day and destroy the correlation that matters most — several
 * losses inside one session are what breaches a daily limit, and they arrive
 * together or not at all.
 */
export function dailyPnl(
  trades: readonly { date: string | null; netPnl: number | null }[],
): number[] {
  const byDay = new Map<string, number>();
  for (const t of trades) {
    if (!t.date) continue;
    byDay.set(t.date, (byDay.get(t.date) ?? 0) + (Number(t.netPnl) || 0));
  }
  return [...byDay.values()];
}

export type RuinBand = 'low' | 'elevated' | 'high';

/** The old client's bands: under 5 low, 5–15 elevated, above 15 high. */
export function ruinBand(pct: number): RuinBand {
  if (pct < 5) return 'low';
  if (pct <= 15) return 'elevated';
  return 'high';
}

// ===========================================================================
// THE SIMULATOR — the same model, opened up
// ===========================================================================
// `riskOfRuin` above answers one question with one number, because that is all
// the account card has room for. This answers the questions a trader asks after
// reading it: how long does passing usually take, how bad does it get, and what
// happens if I change the horizon.
//
// ⚠ IT IS THE SAME WALK, not a second model. Two risk engines that disagree by a
// point would be worse than having only the small one — see the old client's own
// comment about a card that ended up showing two Risk of Ruin figures at once.
// The extra output is recorded from the walk; it does not change it.

/**
 * The horizons the simulator offers, and the one it opens on.
 *
 * 🚨 IT OPENS ON THE CARD'S HORIZON, AND 30 IS IN THE LIST FOR THAT REASON.
 * The old client offers 20/40/60/90 and opens on 40, while its card resamples 30
 * days — so clicking the card's "12.4%" opens a screen headed with a different
 * number, and nothing on either says why. Opening on the same question means the
 * only thing left between them is sampling noise, and changing the horizon then
 * becomes something the trader did on purpose.
 */
export const RUIN_HORIZONS = [20, 30, 40, 60, 90] as const;
export const RUIN_DEFAULT_HORIZON = ROR_HORIZON_DAYS;
/** The simulator's path count. The card runs far fewer — it needs one number. */
export const RUIN_SIM_PATHS = 10000;
/** Under this many real days the odds are directional, and the screen says so. */
export const RUIN_THIN_POOL_DAYS = 10;

export interface RuinSimInput extends RuinInput {
  horizon: number;
  /** Overrides the account's own lock mechanic — the screen's toggle. */
  lock?: boolean;
}

export interface RuinSim {
  sims: number;
  horizon: number;
  /** Reached the profit target first. Always 0 in funded mode. */
  passPct: number;
  /** Touched the trailing floor. */
  blowPct: number;
  /** Neither, at the horizon. In funded mode this is "survived". */
  openPct: number;
  /** Trading days a passing run took — null when nothing passed. */
  medianDaysToPass: number | null;
  p10DaysToPass: number | null;
  p25DaysToPass: number | null;
  p75DaysToPass: number | null;
  /** The lowest balance any single path reached. */
  worstBalance: number;
  /** Balance at each day, 10th/50th/90th percentile. Length horizon + 1. */
  p10: number[];
  p50: number[];
  p90: number[];
  /** Nothing ends a run early — no profit target. */
  funded: boolean;
  /** Real trading days in the resampled pool. */
  poolDays: number;
  /** Mean of the pool. The drift the whole fan hangs off. */
  avgPerDay: number;
  /** The floor the walk starts against, before any trailing. */
  startFloor: number;
  /** The target line, or null in funded mode. */
  targetBalance: number | null;
  lock: boolean;
  lockBuffer: number;
}

/**
 * @returns null on the same two refusals as `riskOfRuin` — too few days, or no
 * drawdown rule to breach. The screen must not invent either.
 */
export function simulateRuin(input: RuinSimInput): RuinSim | null {
  const { days, start, rules, rand, horizon } = input;
  if (days.length < ROR_MIN_DAYS) return null;
  const dd = rules.maxDrawdown;
  if (!(dd > 0)) return null;
  if (!(horizon > 0)) return null;

  const sims = input.sims ?? RUIN_SIM_PATHS;
  const auto = lockParams(rules);
  const lock = input.lock ?? auto.lock;
  const { lockBuffer } = auto;
  const lockCap = start + lockBuffer;
  const funded = !(rules.profitTarget !== null && rules.profitTarget > 0);
  const target = funded ? Infinity : (rules.profitTarget as number);

  let pass = 0; let blow = 0; let open = 0;
  let worstBalance = start;
  const daysToPass: number[] = [];
  // One column per day, one row per path. 91 × 10,000 floats is ~7MB and it is
  // transient — the alternative is re-walking every path once per percentile.
  const bands: Float64Array[] = [];
  for (let d = 0; d <= horizon; d += 1) bands.push(new Float64Array(sims));

  for (let s = 0; s < sims; s += 1) {
    let bal = start;
    let peak = start;
    let floor = start - dd;
    let done = false;
    bands[0]![s] = bal;
    for (let d = 1; d <= horizon; d += 1) {
      // ⚠ A finished path keeps its terminal balance for the rest of the
      // horizon. Leaving it at zero would drag every later percentile toward
      // the floor and draw a fan that no path ever walked.
      if (done) { bands[d]![s] = bal; continue; }
      bal += days[Math.floor(rand() * days.length) % days.length]!;
      if (bal < worstBalance) worstBalance = bal;
      if (bal <= floor) { blow += 1; done = true; bands[d]![s] = bal; continue; }
      if (bal - start >= target) {
        pass += 1; daysToPass.push(d); done = true; bands[d]![s] = bal; continue;
      }
      if (bal > peak) {
        peak = bal;
        floor = peak - dd;
        if (lock && floor > lockCap) floor = lockCap;
      }
      bands[d]![s] = bal;
    }
    if (!done) open += 1;
  }

  const p10: number[] = []; const p50: number[] = []; const p90: number[] = [];
  for (let d = 0; d <= horizon; d += 1) {
    // Typed arrays sort numerically by default — no comparator, and no copy
    // into a plain Array, which at 91 × 10,000 is the difference that shows.
    const col = bands[d]!.sort();
    p10.push(percentileOf(col, 0.10)!);
    p50.push(percentileOf(col, 0.50)!);
    p90.push(percentileOf(col, 0.90)!);
  }
  daysToPass.sort((a, b) => a - b);

  return {
    sims,
    horizon,
    passPct: (pass / sims) * 100,
    blowPct: (blow / sims) * 100,
    openPct: (open / sims) * 100,
    medianDaysToPass: percentileOf(daysToPass, 0.50),
    p10DaysToPass: percentileOf(daysToPass, 0.10),
    p25DaysToPass: percentileOf(daysToPass, 0.25),
    p75DaysToPass: percentileOf(daysToPass, 0.75),
    worstBalance,
    p10,
    p50,
    p90,
    funded,
    poolDays: days.length,
    avgPerDay: days.reduce((sum, x) => sum + x, 0) / days.length,
    startFloor: start - dd,
    targetBalance: funded ? null : start + (rules.profitTarget as number),
    lock,
    lockBuffer,
  };
}

/** Linear-interpolated percentile of an already-sorted series. */
function percentileOf(
  sorted: readonly number[] | Float64Array, q: number,
): number | null {
  if (!sorted.length) return null;
  const i = (sorted.length - 1) * q;
  const lo = Math.floor(i);
  const hi = Math.ceil(i);
  if (lo === hi) return sorted[lo] as number;
  return (sorted[lo] as number)
    + ((sorted[hi] as number) - (sorted[lo] as number)) * (i - lo);
}
